import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import API from '@/api';
import type { GetCampaignResponse } from '@/api/user/types';
import { queryKeys } from '@/api/utils/queryKeys';
import ButtonLink from '@/components/ButtonLink';
import CheckmarkList from '@/components/CheckmarkList/CheckmarkList';
import IdProtectIcon from '@/components/icons/IdProtectIcon';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { showErrorToast } from '@/components/Toast';
import { Campaigns } from '@/constants/SE';
import useModal from '@/hooks/useModal/useModal';
import { routeMap } from '@/routes/routeMap';
import { useModalStore } from '@/store/modalStore';
import Button from '@/ui-kit/components/Button/Button';

const CAMPAIGN_NAME = Campaigns.ID_PROTECT_2_MONTHS_FREE;

type Props = ModalProps<GetCampaignResponse>;

const IdProtectCampaignModal = ({ payload, closeModal }: Props) => {
  const { t } = useTranslation(['Modal', 'ErrorToast']);
  const { openModal } = useModal();
  const { currentModal } = useModalStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: markCampaignAsViewed, isSuccess: isMarkCampaignAsViewedSuccess } = useMutation({
    mutationFn: () => API.user.markCampaignAsViewed(CAMPAIGN_NAME),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaign(CAMPAIGN_NAME) });
    },
    onError: () => {
      showErrorToast(t('ErrorToast:defaultMessage'));
    },
  });

  const { mutate: activateCampaign, isPending: isActivatePending } = useMutation({
    mutationFn: () => API.user.activateCampaign(CAMPAIGN_NAME),
    onSuccess: data => {
      queryClient.setQueryData(queryKeys.userIdProtect, data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaign(CAMPAIGN_NAME) });
      closeModal();
      void navigate(routeMap.idProtect.root);
    },
    onError: () => {
      showErrorToast(t('ErrorToast:defaultMessage'));
    },
  });

  const { mutate: declineCampaign, isPending: isDeclinePending } = useMutation({
    mutationFn: () => API.user.declineCampaign(CAMPAIGN_NAME),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaign(CAMPAIGN_NAME) });
      closeModal();
    },
    onError: () => {
      showErrorToast(t('ErrorToast:defaultMessage'));
    },
  });

  useEffect(() => {
    if (payload.isEligible && !payload.isViewed && !isMarkCampaignAsViewedSuccess) {
      markCampaignAsViewed();
    }
  }, [markCampaignAsViewed, payload, isMarkCampaignAsViewedSuccess]);

  if (!payload.isEligible) {
    return null;
  }

  const handleOpenTermsModal = () => {
    openModal('SE.ID_PROTECT_TERMS', {
      closeAction: () => {
        openModal('SE.ID_PROTECT_CAMPAIGN', {
          isPersistedFromRedirect: currentModal?.options.isPersistedFromRedirect,
          payload,
        });
      },
    });
  };

  return (
    <Modal
      title={t('Modal:IdProtectCampaign.title')}
      topLeftContent={<IdProtectIcon className="text-icon-brand" />}
    >
      <div className="flex flex-col gap-4">
        <p>{t('Modal:IdProtectCampaign.body')}</p>
        <p className="text-caption-regular text-text-subtle">
          {t('Modal:IdProtectCampaign.costAfter', { amount: payload.costPerMonth })}
        </p>
      </div>
      <CheckmarkList className="my-8">
        <CheckmarkList.Item>{t('Modal:IdProtectCampaign.features.feature1')}</CheckmarkList.Item>
        <CheckmarkList.Item>{t('Modal:IdProtectCampaign.features.feature2')}</CheckmarkList.Item>
        <CheckmarkList.Item>{t('Modal:IdProtectCampaign.features.feature3')}</CheckmarkList.Item>
        <CheckmarkList.Item>{t('Modal:IdProtectCampaign.features.feature4')}</CheckmarkList.Item>
      </CheckmarkList>
      <p className="disclaimer">
        <Trans
          t={t}
          i18nKey="Modal:IdProtectCampaign.disclaimer"
          components={{
            idProtectTerms: <ButtonLink onClick={handleOpenTermsModal} />,
          }}
        />
      </p>
      <ModalActions className="mt-2!">
        <Button onClick={() => activateCampaign()} loading={isActivatePending} disabled={isDeclinePending}>
          {t('Modal:IdProtectCampaign.buttons.accept')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => declineCampaign()}
          loading={isDeclinePending}
          disabled={isActivatePending}
        >
          {t('Modal:IdProtectCampaign.buttons.decline')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default IdProtectCampaignModal;
